import { isEmpty } from '@fruitful/fruitils';
import React, { forwardRef } from 'react';
import { type Input as BaseInput } from 'tamagui';

import { Input, type InputProps } from './Input';

const NON_NUMERIC_REGEX = /[^(\d*)]/g;
const PERCENT_SIGN_WITH_SPACE = ' %';

const formatPercentageOnInputChange = (
    previousValue: string,
    nextValue: string,
    maxValue: number = 100,
): string => {
    // Remove anything other than digits (whole numbers only allowed)
    // Also remove the % and space if present
    nextValue = nextValue
        .replace(NON_NUMERIC_REGEX, '')
        .replace(/\s*%\s*/g, '');

    const numericValue = parseInt(nextValue, 10);
    if (!Number.isNaN(numericValue) && numericValue > maxValue) {
        // Don't allow percentage over maxValue
        return previousValue;
    }

    if (isEmpty(nextValue)) {
        // Return empty when empty
        return '';
    }

    // Return number with % postfix and space: "50 %"
    return `${nextValue}${PERCENT_SIGN_WITH_SPACE}`;
};

export interface PercentInputProps extends Omit<
    InputProps,
    'onChangeText' | 'value'
> {
    value?: string;
    onChangeText?: (formattedValue: string) => void;
    maxValue?: number;
}

export const PercentInput = forwardRef<BaseInput, PercentInputProps>(
    (
        {
            value = '',
            onChangeText,
            placeholder = '%',
            inputMode = 'numeric',
            keyboardType = 'number-pad',
            autoComplete = 'off',
            maxValue = 100,
            onFocus,
            ...props
        },
        ref,
    ) => {
        const handleChangeText = (text: string) => {
            const currentDigits = value?.replace(/\s*%\s*$/, '') || '';

            const newDigits = text
                .replace(NON_NUMERIC_REGEX, '')
                .replace(/\s*%\s*/g, '');

            // If the new text has the same digits as current but the text doesn't end with " %",
            // the user is trying to backspace the suffix - remove the last digit instead
            if (
                newDigits === currentDigits &&
                currentDigits.length > 0 &&
                !text.endsWith(' %')
            ) {
                // User is backspacing the suffix (space or %) - remove last digit
                const digitsWithoutLast = currentDigits.slice(0, -1);
                const formattedValue = formatPercentageOnInputChange(
                    '',
                    digitsWithoutLast,
                    maxValue,
                );
                onChangeText?.(formattedValue);
                return;
            }

            const formattedValue = formatPercentageOnInputChange(
                currentDigits,
                text,
                maxValue,
            );
            onChangeText?.(formattedValue);
        };

        return (
            <Input
                ref={ref}
                value={value}
                onChangeText={handleChangeText}
                onFocus={onFocus}
                placeholder={placeholder}
                inputMode={inputMode}
                keyboardType={keyboardType}
                autoComplete={autoComplete}
                {...props}
            />
        );
    },
);
